import { legacyLocalLabel } from '../../../../utilities/legacyDatePresentation';
import { useCallback } from "react";
import React, { useEffect, useState, useRef } from 'react';
import { Trash2, Edit2, ChevronLeft } from 'lucide-react';
import { GetAllPlanFeatureReq, PlanFeatureResponse } from '../../../../interfaces';
import { useAuth } from '../../../../firebase/AuthProvider';
import {
    getAllPlanFeatureAsync,
    deletePlanFeatureAsync,
} from '../../../../services/PlanService/PlanFeatureService';
import CreatePlanFeatureModal from './modals/CreatePlanFeatureModal';
import EditPlanFeatureModal from './modals/EditPlanFeatureModal';
import { Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { AxiosError } from 'axios';
import { Button } from 'react-bootstrap';
import { showConfirmationDialog } from '../../../modal/ConfimationModal/Confimation';
import Pagination from '../../../ui/Pagination/Pagination';
import { PaginationDto } from '../../../../interfaces/Employee/Employee';
import { useNavigate, useParams } from 'react-router-dom';


const PlanFeatures = () => {
    const { currentUser } = useAuth();
    const navigate = useNavigate();
    const { planId } = useParams<{ planId: string }>();

    const [features, setFeatures] = useState<PlanFeatureResponse[]>([]);
    const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
    const [deletingId, setDeletingId] = useState<string | null>(null);
    const [editingFeature, setEditingFeature] = useState<PlanFeatureResponse | null>(null);
    const [reload, setReload] = useState<boolean>(true);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [totalCount, setTotalCount] = useState<number>(0);
    const [pagination, setPagination] = useState<PaginationDto>({ pageNumber: 1, pageSize: 10 });
    const [sortKey, setSortKey] = useState<string | null>(null);
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

    const controllersRef = useRef<Map<string, AbortController>>(new Map());

    const loadFeatures = useCallback(async () => {
        if (!planId) return;

        const controller = new AbortController();
        controllersRef.current.get('getFeatures')?.abort();
        controllersRef.current.set('getFeatures', controller);
        setIsLoading(true);

        try {
            const token = await currentUser?.getIdToken();
            if (!token) return;

            const payload: GetAllPlanFeatureReq = {
                pageNumber: pagination.pageNumber,
                pageSize: pagination.pageSize,
                planId: planId,
                sortBy: sortKey,
                sortOrder: sortDirection,
            };

            const res = await getAllPlanFeatureAsync(token, payload, controller.signal);
            if (res.success) {
                setFeatures(res.data.items);
                setTotalCount(res.data.totalCount);
            } else {
                toast.error(res.message);
            }
        } catch (err: any) {
            if (err.name === 'AbortError') return;
            toast.error(
                err instanceof AxiosError
                    ? err.response?.data.message
                    : err.message || 'Error fetching features'
            );
        } finally {
            controllersRef.current.delete('getFeatures');
            setIsLoading(false);
        }
    }, [currentUser, planId, pagination, sortKey, sortDirection]);

useEffect(() => {
        const controllers = controllersRef.current;
        if (planId) {
            loadFeatures();
        }

        return () => {
            controllers.forEach((c) => c.abort());
            controllers.clear();
        };
    }, [reload, pagination, sortKey, sortDirection, planId, loadFeatures]);

    const handleOpenCreate = () => setShowCreateModal(true);
    const handleCloseCreate = () => setShowCreateModal(false);

    const handleEdit = (feature: PlanFeatureResponse) => {
        setEditingFeature(feature);
    };
    const handleCloseEdit = () => setEditingFeature(null);

    const handleDelete = async (featureId: string) => {
        const isConfirm = await showConfirmationDialog('Are you sure?', 'Do you want to delete this?');
        if (!isConfirm) return;

        const controller = new AbortController();
        controllersRef.current.set(`delete-${featureId}`, controller);
        setDeletingId(featureId);

        try {
            const token = await currentUser?.getIdToken();
            if (!token) return;

            const res = await deletePlanFeatureAsync(token, featureId, controller.signal);
            if (res.success) {
                toast.success(res.message);
                setReload(!reload);
            } else {
                toast.error(res.message);
            }
        } catch (err: any) {
            if (err.name === 'AbortError') return;
            toast.error(
                err instanceof AxiosError
                    ? err.response?.data.message
                    : err.message || 'Error deleting feature'
            );
        } finally {
            controllersRef.current.delete(`delete-${featureId}`);
            setDeletingId(null);
        }
    };



    const onPageChange = (pageNum: number) => {
        setPagination((prev) => ({ ...prev, pageNumber: pageNum }));
    };

    const resetPageSize = (pageNumber: number, pageSize: number) => {
        setPagination({ pageNumber, pageSize });
    };

    const handleSort = (key: string) => {
        if (sortKey === key) {
            setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortKey(key);
            setSortDirection('asc');
        }
        setPagination((prev) => ({ ...prev, pageNumber: 1 }));
    };

    const getSortIcon = (key: string) => {
        if (sortKey !== key) return <span className="text-gray-300 ml-1">⇅</span>;
        return <span className="text-[#7E57C2] ml-1">{sortDirection === 'asc' ? '▲' : '▼'}</span>;
    };

    return (
        <div className="common_card">
            <div className="w-full flex flex-col flex-1 my-2">
                <div className="flex justify-between items-center px-5 py-3">
                    <div className="flex items-center space-x-2">
                        <button
                            type="button"
                            onClick={() => navigate("/superadmin/plans")}
                            className="text-gray-500 hover:text-gray-700"
                            title="Back to plans"
                        >
                            <ChevronLeft className="w-5 h-5" />
                        </button>
                        <h2 className="text-lg font-semibold">Plan Features {planId && `(plan ${planId})`}</h2>
                    </div>
                    <Button onClick={handleOpenCreate} className="px-3 py-1 text-white rounded ">
                        + Add Feature
                    </Button>
                </div>
                <div className="w-full rounded-lg border border-gray-200 bg-white flex-1 flex flex-col">
                    <div className="w-full overflow-x-auto flex-1">
                        <table className="w-full text-left border-collapse min-w-[900px]">
                            <thead className="bg-gray-50/80 border-b border-gray-200 sticky top-0 z-10">
                                <tr>
                                    <th
                                        className="px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase cursor-pointer"
                                        onClick={() => handleSort('featureName')}
                                    >
                                        Feature Name {getSortIcon('featureName')}
                                    </th>
                                    <th
                                        className="px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase cursor-pointer"
                                        onClick={() => handleSort('featureKey')}
                                    >
                                        Key {getSortIcon('featureKey')}
                                    </th>

                                    <th
                                        className="px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase cursor-pointer"
                                        onClick={() => handleSort('isEnabled')}
                                    >
                                        Enabled {getSortIcon('isEnabled')}
                                    </th>
                                    <th
                                        className="px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase cursor-pointer"
                                        onClick={() => handleSort('limitValue')}
                                    >
                                        Limit {getSortIcon('limitValue')}
                                    </th>
                                    <th
                                        className="px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase cursor-pointer"
                                        onClick={() => handleSort('isActive')}
                                    >
                                        Active {getSortIcon('isActive')}
                                    </th>
                                    <th className="px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase">Created</th>
                                    <th className="px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase">Updated</th>
                                    <th className="px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 bg-white">
                                {features.length === 0 ? (
                                    isLoading ? (
                                        <tr>
                                            <td colSpan={7} className="px-5 py-6 text-center">
                                                <Spinner animation="border" size="sm" />
                                            </td>
                                        </tr>
                                    ) : (
                                        <tr>
                                            <td colSpan={7} className="px-5 py-6 text-center text-gray-500">
                                                No features found.
                                            </td>
                                        </tr>
                                    )
                                ) : (
                                    features.map((feature) => (
                                        <tr key={feature.id} className="hover:bg-gray-50/80">
                                            <td className="px-5 py-3.5">{feature.featureName}</td>
                                            <td className="px-5 py-3.5">{feature.featureKey}</td>
                                            <td className="px-5 py-3.5">{feature.isEnabled ? 'Yes' : 'No'}</td>
                                            <td className="px-5 py-3.5">{feature.limitValue ?? '-'}</td>
                                            <td className="px-5 py-3.5">{feature.isActive ? 'Active' : 'Inactive'}</td>
                                            <td className="px-5 py-3.5">
                                                {legacyLocalLabel(feature.createDate, "MM/dd/yyyy")}
                                            </td>
                                            <td className="px-5 py-3.5">
                                                {legacyLocalLabel(feature.updateDate, "MM/dd/yyyy")}
                                            </td>
                                            <td className="px-5 py-3.5 text-right">
                                                <div className="flex items-center justify-end space-x-3">
                                                    <button
                                                        type="button"
                                                        title="Edit"
                                                        onClick={() => handleEdit(feature)}
                                                        className="text-gray-500 hover:text-gray-700"
                                                    >
                                                        <Edit2 className="w-4 h-4" />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        title="Delete"
                                                        onClick={() => handleDelete(feature.id)}
                                                        className="text-gray-500 hover:text-gray-700 flex items-center"
                                                    >
                                                        {deletingId === feature.id ? (
                                                            <Spinner animation="border" size="sm" />
                                                        ) : (
                                                            <Trash2 className="w-4 h-4" />
                                                        )}
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
            {totalCount > 0 && (
                <Pagination
                    currentPage={pagination.pageNumber}
                    totalPages={Math.ceil(totalCount / pagination.pageSize)}
                    onPageChange={onPageChange}
                    pageSize={pagination.pageSize}
                    resetPageSize={resetPageSize}
                />
            )}

            {showCreateModal && (
                <CreatePlanFeatureModal
                    isOpen={showCreateModal}
                    onClose={handleCloseCreate}
                    onSuccess={() => setReload(!reload)}
                    planId={planId!}
                />
            )}
            {editingFeature && (
                <EditPlanFeatureModal
                    isOpen={Boolean(editingFeature)}
                    onClose={handleCloseEdit}
                    onSuccess={() => setReload(!reload)}
                    feature={editingFeature}
                />
            )}
        </div>
    );
};

export default PlanFeatures;